import {useEffect, useRef, useState, type ReactNode} from "react";
import {flexRender, type Row, type RowData} from "@tanstack/react-table";
import {clsx} from "clsx";
import {Table, type TableSize} from "@/primitives/Table";
import {FA6Icon} from "@/primitives/FA6Icon";
import {DataTableSortableColumnHeader} from "./DataTableSortableColumnHeader";
import {useDataTableContext} from "./DataTableRoot";
import type {DataTableFeatures} from "./useDataTable";
import styles from "./DataTable.module.css";
import {clickedInsideTextElement} from "@/shared/utils/misc";

/* Marks the padding rows so they can be excluded when measuring a real row's height. */
const FILLER_ROW_SLOT = "data-table-filler-row";

/** Bootstrap alignment utility for a column's `meta.align`. */
function alignClass(align: "left" | "center" | "right" | undefined): string | undefined {
	if (align === "center") return "text-center";
	if (align === "right") return "text-right";
	return undefined;
}

interface DataTableContentProps<TData extends RowData> extends Omit<React.ComponentProps<"table">, "children"> {
	/** Forwarded to `Table.Root` */
	size?: TableSize;
	striped?: boolean;
	hover?: boolean;
	bordered?: boolean;
	/** Extra classes for the `.table-responsive` container. Passed through. */
	containerClassName?: string;
	/**
	 * `"fixed"` switches the table to `table-layout: fixed`, so column widths no longer
	 * depend on the rendered cell content — paging cannot shift them sideways any more.
	 * Columns then share the width equally unless a column def sets an explicit `size`.
	 */
	layout?: "auto" | "fixed";
	/**
	 * Pad a short last page with blank rows so the table keeps its height while paging.
	 * Opt-in: it only looks right when every row is the same height — with wrapping or
	 * multi-line cells the padding cannot match the real rows.
	 */
	stableHeight?: boolean;
	/**
	 * Scrolls the rows inside a box of this height while the column headers stay pinned to
	 * its top — the alternative to paging through a long list. Any CSS length; a number is
	 * pixels. A fixed height (not a max) keeps the surrounding layout still while filtering.
	 */
	scrollHeight?: number | string;
	/** Render a footer row group from the columns' `footer` defs. Off by default. */
	showFooter?: boolean;
	/** Per-row attributes (e.g. a class for archived/conflicted rows). */
	rowProps?: (row: Row<DataTableFeatures, TData>) => React.ComponentProps<"tr">;
	/** Double-click a row — the legacy open-for-edit interaction. */
	onRowDoubleClick?: (data: TData, row: Row<DataTableFeatures, TData>) => void;
	/** Overrides the Root's empty label for this table. */
	renderEmpty?: () => ReactNode;
	/** Accessible name for the table, rendered as a `<caption>`. */
	caption?: ReactNode;
}

function DataTableContent<TData extends RowData>({
	size,
	striped,
	hover,
	bordered,
	containerClassName,
	layout = "auto",
	stableHeight = false,
	scrollHeight,
	showFooter = false,
	rowProps,
	onRowDoubleClick,
	renderEmpty,
	caption,
	...tableProps
}: DataTableContentProps<TData>) {
	const {table, loading} = useDataTableContext<TData>("Content");
	const rows = table.getRowModel().rows;
	const columnCount = table.getVisibleLeafColumns().length;
	// pad short (last) pages to the page size so the table height stays stable while paging
	const paginated = Boolean(table.options.manualPagination || table.options.features.paginatedRowModel);
	const fillerRowCount =
		stableHeight && paginated && rows.length > 0 ? Math.max(table.state.pagination.pageSize - rows.length, 0) : 0;
	const body = useRef<HTMLTableSectionElement>(null);
	const [rowHeight, setRowHeight] = useState<number | null>(null);

	/*
	 * A filler row holding only a blank is as tall as one line of text, while real rows
	 * can be much taller (two-line cells, inputs, buttons) — so padding alone does not
	 * keep the height stable. Measure the rendered rows and give the fillers the same
	 * height. Gating on `rows` covers every content change (paging, filtering) with one
	 * synchronous read; the ResizeObserver covers organic size changes (wrapping, zoom)
	 * without measuring on every render. The epsilon guard keeps the
	 * measure → setState → resize loop from ping-ponging.
	 */
	useEffect(() => {
		if (!stableHeight || body.current == null) {
			return;
		}
		const element = body.current;
		const measure = () => {
			const contentRows = element.querySelectorAll<HTMLTableRowElement>(`tr:not([data-slot="${FILLER_ROW_SLOT}"])`);
			if (contentRows.length === 0) {
				return;
			}
			const measured = Array.from(contentRows).reduce((total, row) => total + row.getBoundingClientRect().height, 0) / contentRows.length;
			setRowHeight((current) => (current != null && Math.abs(current - measured) < 1 ? current : measured));
		};
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(element);
		return () => observer.disconnect();
	}, [stableHeight, rows]);

	return (
		<div className={styles.content}>
			{/* always mounted: screen readers announce live regions reliably only when
          the observed text changes, so we toggle the text, not the region */}
			<div className="sr-only" role="status" aria-live="polite">
				{loading ? messages["datatable.loading"] : ""}
			</div>
			{loading && (
				<div data-slot="data-table-overlay" className={styles.overlay} aria-hidden="true">
					<FA6Icon icon="spinner" animation="spin" className={styles.overlaySpinner} />
				</div>
			)}
			<Table.Root
				size={size}
				striped={striped}
				hover={hover}
				bordered={bordered}
				containerClassName={containerClassName}
				scrollHeight={scrollHeight}
				{...tableProps}
				className={clsx(
					layout === "fixed" && styles.fixedLayout,
					scrollHeight != null && styles.stickyHeader,
					tableProps.className,
				)}
				aria-busy={loading || undefined}
			>
				{caption != null && <Table.Caption>{caption}</Table.Caption>}
				<Table.Header>
					{table.getHeaderGroups().map((headerGroup) => (
						<Table.Row key={headerGroup.id}>
							{headerGroup.headers.map((header) => {
								const {align, headerClassName} = header.column.columnDef.meta ?? {};
								const canSort = header.column.getCanSort();
								const sorted = header.column.getIsSorted();
								const ariaSort = sorted === "asc" ? "ascending" : sorted === "desc" ? "descending" : "none";
								return (
									<Table.Head
										key={header.id}
										colSpan={header.colSpan > 1 ? header.colSpan : undefined}
										className={clsx(alignClass(align), headerClassName)}
										/* only widths the column def asked for — TanStack's default size would pin every column */
										style={header.column.columnDef.size != null ? {width: header.getSize()} : undefined}
										aria-sort={canSort ? ariaSort : undefined}
									>
										{header.isPlaceholder ? null : canSort ? (
											<DataTableSortableColumnHeader header={header} />
										) : (
											flexRender(header.column.columnDef.header, header.getContext())
										)}
									</Table.Head>
								);
							})}
						</Table.Row>
					))}
				</Table.Header>

				<Table.Body ref={body}>
					{rows.length === 0 ? (
						<Table.Row>
							<Table.Cell colSpan={columnCount} className={styles.stateCell}>
								{renderEmpty ? renderEmpty() : messages["datatable.empty"]}
							</Table.Cell>
						</Table.Row>
					) : (
						rows.map((row) => (
							<Table.Row
								key={row.id}
								data-state={row.getIsSelected() ? "selected" : undefined}
								onMouseDown={onRowDoubleClick ? (event) => {
									// Preserve native text selection; only cell space activates the row.
									if (event.detail > 1 && !clickedInsideTextElement(event.target as Element, event.clientX, event.clientY)) {
										event.preventDefault();
									}
								} : undefined}
								onDoubleClick={onRowDoubleClick ? (event) => {
									if (!clickedInsideTextElement(event.target as Element, event.clientX, event.clientY)) {
										onRowDoubleClick(row.original, row);
									}
								} : undefined}
								{...rowProps?.(row)}
							>
								{row.getVisibleCells().map((cell) => {
									const {align, cellClassName} = cell.column.columnDef.meta ?? {};
									return (
										<Table.Cell key={cell.id} className={clsx(alignClass(align), cellClassName)}>
											{flexRender(cell.column.columnDef.cell, cell.getContext())}
										</Table.Cell>
									);
								})}
							</Table.Row>
						))
					)}
					{Array.from({length: fillerRowCount}, (_, index) => (
						<Table.Row
							key={index}
							data-slot={FILLER_ROW_SLOT}
							aria-hidden="true"
							className={styles.fillerRow}
							style={rowHeight != null ? {height: rowHeight} : undefined}
						>
							<Table.Cell colSpan={columnCount}>&nbsp;</Table.Cell>
						</Table.Row>
					))}
				</Table.Body>

				{showFooter && (
					<Table.Footer>
						{table.getFooterGroups().map((footerGroup) => (
							<Table.Row key={footerGroup.id}>
								{footerGroup.headers.map((header) => {
									const {align, headerClassName} = header.column.columnDef.meta ?? {};
									return (
										<Table.Cell
											key={header.id}
											colSpan={header.colSpan > 1 ? header.colSpan : undefined}
											className={clsx(alignClass(align), headerClassName)}
										>
											{header.isPlaceholder
												? null
												: flexRender(header.column.columnDef.footer, header.getContext())}
										</Table.Cell>
									);
								})}
							</Table.Row>
						))}
					</Table.Footer>
				)}
			</Table.Root>
		</div>
	);
}

export {DataTableContent};
export type {DataTableContentProps};
